import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import {
  Menu,
  MessageSquare,
  Store,
  FileText,
  Star,
  Bell,
  Wallet,
  User,
  LogIn,
  LogOut,
  Search,
  History
} from "lucide-react";
import { useState, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useUserStore } from "@/store/userStore";
import { useToast } from "@/hooks/use-toast";
import { Input } from "./ui/input";
import { ROUTES } from "@/utils/routes";
import { useAuthStore } from "@/store/authStore";
import { Axios, socket } from "@/utils/axios";
import { useFreelancerStore } from "@/store/freelancerStore";
import { useEnterpriseStore } from "@/store/enterpriseStore";
import { selectTotalUnread, useMessageStore } from "@/store/messageStore";
import NotificationPopover from "@/pages/NotificationPopover";
import { getImageUrl } from "@/lib/utils";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthPage = location.pathname === ROUTES.AUTH;
  const isForgotPassPage = location.pathname === ROUTES.FORGOT_PASSWORD;
  const isMobile = useIsMobile();
  const { isAuthenticated, user, logout: userLogout, unReadMessages } = useUserStore();
  const { logout: authLogout } = useAuthStore();
  const { clearFreelancerProfile,profile: FreelancerProfile } = useFreelancerStore();
  const { clearEnterpriseProfile, profile: enterpriseProfile } = useEnterpriseStore();
  const { toast } = useToast();

 const totalUnread = useMessageStore(selectTotalUnread);

  // Handle navbar background on scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    socket.on("notification:unreadCountUpdated", (unreadCount) => {
      setNotificationCount(unreadCount);
    });
    return () => {
      socket.off("notification:unreadCountUpdated");
    };
  }, [user]);

  const handleLogout = async () => {
    try {
      const response = await Axios.post("/api/auth/logout");
      authLogout();
      userLogout();
      clearFreelancerProfile()
      clearEnterpriseProfile();
      toast({
        title: "Logged out",
        description: "You have been logged out successfully",
        variant: "default",
      });
      navigate("/");
    } catch (error) {
      toast({
        title: "Logout failed",
        description: "An error occurred while logging out. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Check if current page is home
  const isHomePage = location.pathname === "/";
  const redirectTo = isAuthPage ? "/" : "/dashboard";

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${isScrolled
          ? "bg-white/90 backdrop-blur-md shadow-md py-2"
          : "bg-transparent py-4"
          }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to={redirectTo} className="flex items-center gap-2 group">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-r from-emerald-500 to-sky-500 flex items-center justify-center shadow-lg group-hover:shadow-emerald-500/25 transition-all duration-300 rotate-3">
                <span className="text-white font-bold text-xl">D</span>
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-transparent">
                Freelance Hub
              </span>
            </Link>

            {/* {!isAuthPage &&
              !isHomePage &&
              !isForgotPassPage &&
              (!isMobile ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4">
                  <div className="lg:col-span-5 col-span-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <Input
                        className="pl-10 pr-4 py-2 w-full border-gray-200 focus:border-sky-300 focus:ring-sky-200"
                        placeholder="Search by skill, project or keyword..."
                      />
                    </div>
                  </div>
                  <div className="lg:col-span-5 col-span-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <Input
                        className="pl-10 pr-4 py-2 w-full border-gray-200 focus:border-sky-300 focus:ring-sky-200"
                        placeholder="Location / Remote"
                      />
                    </div>
                  </div>
                  <div className="lg:col-span-2 sm:col-span-2 col-span-1">
                    <Button
                      variant="outline"
                      className="w-full text-white bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 transition-all duration-300"
                    >
                      Reset
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setIsMenuOpen(true)}
                  className="p-2 text-gray-600 hover:text-emerald-600 ms-24 md:hidden"
                >
                  <Menu className="w-6 h-6" />
                </button>
              ))} */}

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              <Link
                to="/projects"
                className="text-gray-600 hover:text-emerald-600 transition-colors px-3 py-2 rounded-lg hover:bg-gray-50 text-sm"
              >
                Projects
              </Link>
              {isAuthenticated ? (
                <>
                  <Link
                    to="/messages"
                    className="text-gray-600 hover:text-emerald-600 transition-colors flex items-center gap-1 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm"
                  >
                    <MessageSquare className="w-4 h-4" />
                    <span>Messages</span>
                    {totalUnread > 0 && <span className="w-4 h-4 text-center bg-red-300 rounded-full animate-pulse">{totalUnread}</span>}
                  </Link>

                  {/* Notifications */}
                  <span className="relative p-2 pb-0 text-gray-600 hover:text-emerald-600 transition-colors rounded-lg hover:bg-gray-50">
                    <NotificationPopover notificationCount={notificationCount} />
                  </span>

                  <Link
                    to="/wallet"
                    className="text-gray-600 hover:text-emerald-600 transition-colors flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm"
                  >
                    <Wallet className="w-4 h-4" />
                    <span>Wallet</span>
                  </Link>

                  <Link
                    to="/paymentHistory"
                    className="text-gray-600 hover:text-emerald-600 transition-colors flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm"
                  >
                    <History className="w-4 h-4" />
                    <span>Payment History</span>
                  </Link>

                  <Link to="/profile" className="p-1">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 hover:bg-emerald-200 transition-colors">
                      <img className="rounded-full w-full h-full object-cover" src={getImageUrl(enterpriseProfile?.logo || FreelancerProfile?.profilePicture) || "https://ui-avatars.com/api/?name=" + encodeURIComponent(enterpriseProfile?.companyName || FreelancerProfile?.fullName)} alt="" srcSet="" />
                    </div>
                  </Link>

                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-2 flex items-center gap-1.5"
                    onClick={handleLogout}
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </Button>
                </>
              ) : (
                <Link to={ROUTES.AUTH} className="ml-2">
                  <Button
                    variant="default"
                    size="sm"
                    className="bg-gradient-to-r from-emerald-600 to-sky-600 hover:from-emerald-700 hover:to-sky-700 text-white shadow-lg hover:shadow-emerald-500/25 transition-all duration-300 rounded-full px-4 flex items-center gap-1.5"
                  >
                    <LogIn className="w-4 h-4" />
                    Sign In
                  </Button>
                </Link>
              )}
            </div>

            {/* Mobile navigation elements */}
            {isMobile && !isAuthPage && (
              <div className="flex items-center gap-2 md:hidden">
                {isAuthenticated ? (
                  <>
                    <Link to="/profile" className="p-1">
                      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                        <User className="w-4 h-4" />
                      </div>
                    </Link>
                    <Button variant="outline" size="sm" onClick={handleLogout}>
                      <LogOut className="w-4 h-4" />
                    </Button>
                  </>
                ) : (
                  <Link to={ROUTES.AUTH}>
                    <Button
                      variant="default"
                      size="sm"
                      className="bg-gradient-to-r from-emerald-600 to-sky-600 text-white shadow-md rounded-full px-3 flex items-center gap-1.5"
                    >
                      <LogIn className="w-4 h-4" />
                      Sign In
                    </Button>
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </nav>
      {/* Add spacer for all pages to prevent content from being hidden under navbar */}
      {!isHomePage && <div className="h-24"></div>}

      {isMobile && isMenuOpen && (
        <div className="fixed inset-0 z-50 bg-white p-6 flex flex-col gap-4 overflow-hidden">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Search</h2>
            <button
              onClick={() => setIsMenuOpen(false)}
              className="text-gray-500 text-2xl"
            >
              ✕
            </button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Input
              className="pl-10 pr-4 py-2 w-full border-gray-200"
              placeholder="Search by skill, project or keyword..."
            />
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Input
              className="pl-10 pr-4 py-2 w-full border-gray-200"
              placeholder="Location / Remote"
            />
          </div>
          <Button
            variant="outline"
            className="w-full text-white bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 transition-all duration-300"
          >
            Reset
          </Button>
        </div>
      )}
    </>
  );
};

export default Navbar;
